import React, { useState, useMemo } from "react";
import "../styles/login.css"
import axios from "axios";
import { toast } from '../utils/toast';
import { useSubmitGuard } from '../utils/useSubmitGuard';
import { errorMessage } from '../utils/errorMessage';
import { Link, useNavigate } from "react-router-dom";
import { API_DETAILS, USER_TYPES, subscriptionStatus } from "../App";
import Loader from "../utils/loader";
import WaitlistForm from "./WaitlistForm";
import { US_STATES, isServedState, stateName } from "../utils/usStates";
import "../styles/StateGate.css";

const Signupoptions= () => {

    const navigate = useNavigate();
    const [activeLoader, setActiveLoader] = useState(false);
    const [registering, guard] = useSubmitGuard();
    const [setOpenGeneralForm] = useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const [focusedField, setFocusedField] = React.useState(null);
    const [step, setStep] = useState(1);
    // Where the person lives, asked before anything else. Youro can only treat
    // patients physically in a state its providers are licensed in, so nobody
    // outside one is asked for a name, a password or anything clinical: they are
    // offered the waitlist instead. `gateChoice` is the dropdown; `residence` is
    // set once they continue.
    const [gateChoice, setGateChoice] = useState("");
    const [residence, setResidence] = useState("");
    const outsideServedArea = Boolean(residence) && !isServedState(residence);

    const confirmResidence = (e) => {
        e.preventDefault();
        if (!gateChoice) return;
        setResidence(gateChoice);
        if (isServedState(gateChoice)) {
          // The address state in step 2 is this one, and cannot be changed there.
          setFormValues((prev) => ({ ...prev, state: gateChoice }));
        }
        window.scrollTo(0, 0);
    };

    const changeResidence = () => {
        setResidence("");
        setFormValues((prev) => ({ ...prev, state: "" }));
    };
    const [signupType, setSignupType] = useState("myself");
    const [errors, setErrors] = useState({});

    const [formValues, setFormValues] = useState({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      patfirstName: "",
      patlastName: "",
      relation: "",
      dateOfBirth: "",
      gender: "",
      address: "",
      address2: "",
      city: "",
      state: "",
      zipCode: "",
      agreedPrivacy: false,
      agreedTelehealth: false,
      agreedTerms: false,
    });

    const isStep1Filled = useMemo(() => {
        const commonFields =
          formValues.firstName &&
          formValues.lastName &&
          formValues.email &&
          formValues.phoneNumber &&
          formValues.password &&
          formValues.confirmPassword;

        if (signupType === "myself") {
          return commonFields;
        } else {
          return commonFields && formValues.patfirstName && formValues.patlastName && formValues.relation;
        }
    }, [formValues, signupType]);

    const validateStep1 = () => {
        let newErrors = {};
        let isValid = true;

        const emailRegex = /\S+@\S+\.\S+/;
        if (!emailRegex.test(formValues.email)) {
          newErrors.email = "Please enter a valid email address.";
          isValid = false;
        }

        if (formValues.phoneNumber.length !== 10) {
          newErrors.phoneNumber = "Please enter a valid phone number.";
          isValid = false;
        }

        if (formValues.password !== formValues.confirmPassword) {
          newErrors.confirmPassword = "Passwords do not match.";
          isValid = false;
        }

        if (formValues.password.length < 8) {
          newErrors.password = "Password must be at least 8 characters.";
          isValid = false;
        }

        setErrors(newErrors);
        return isValid;
      };

    const handleStep1Submit = (e) => {
        e.preventDefault();

        // Run validation checks
        if (validateStep1()) {
          setStep(2);
          window.scrollTo(0, 0);

          // Example navigation logic
          /* setActiveLoader(true);
          setTimeout(() => {
              setActiveLoader(false);
              if (signupType === "myself") navigate('/signupmyself');
              else navigate('/signupforfamilymember');
          }, 200);
          */
        } else {
        }
      };

    const isStep2Valid = useMemo(() => {
        return (
          formValues.dateOfBirth &&
          formValues.gender &&
          formValues.address &&
          formValues.city &&
          formValues.state &&
          formValues.zipCode &&
          formValues.agreedPrivacy &&
          formValues.agreedTelehealth &&
          formValues.agreedTerms
        );
    }, [formValues]);

    const validateStep2 = () => {
        let newErrors = {};
        let isValid = true;

        if (formValues.zipCode.length !== 5) {
          newErrors.zipCode = "Please enter a valid zip code.";
          isValid = false;
        }

        setErrors(newErrors);
        return isValid;
      };

    const handleFinalSubmit = guard(() => {
        if (validateStep2()) {
            setActiveLoader(true);
            let usrData = { ...formValues };
            usrData['userType'] = USER_TYPES.user;
            usrData['subscriptionStatus'] = subscriptionStatus.active;
            usrData['hasInsurance'] = false;
            delete usrData.confirmPassword;
            delete usrData.agreedPrivacy;
            delete usrData.agreedTelehealth;
            delete usrData.agreedTerms;
            delete usrData.address2;
            if (usrData.patfirstName === "") {
              delete usrData.patfirstName;
            } else {
              usrData.firstName = usrData.patfirstName;
              delete usrData.patfirstName;
            }

            if (usrData.patlastName === "") {
              delete usrData.patlastName;
            } else {
              usrData.lastName = usrData.patlastName;
              delete usrData.patlastName;;
            }

            const config = {
                headers: {
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Methods': '*',
                    'Content-Type': 'application/json'
                }
            };
            return axios.post(API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + "/auth/register", usrData, config)
                .then((res) => {
                    setActiveLoader(false);

                    toast.success("Your account is created. Next, verify your email address.");
                    navigate("/verify-email", { state: { userEmail: usrData.email } });
                })
                .catch((err) => {
                    setActiveLoader(false);
                    // The API reports handled problems as errorMessage (a duplicate
                    // account, for instance) but unhandled ones as detail. Reading
                    // only the first meant every unexpected failure reached the
                    // patient as "An unknown error occurred", with nothing to act on.
                    const data = err.response?.data || {};
                    const raw = data.errorMessage || data.detail || data.message || "";

                    // Turn the one database error a patient can actually cause into
                    // something they can fix, rather than showing them SQL.
                    const friendly = /Data too long for column '([a-z_]+)'/i.exec(raw);
                    const fieldLabels = {
                        email: "email address",
                        address: "address",
                        first_name: "first name",
                        last_name: "last name",
                        city: "city",
                    };
                    const message = friendly
                        ? `Your ${fieldLabels[friendly[1]] || friendly[1].replace(/_/g, " ")} is too long for us to store. Please shorten it and try again.`
                        : errorMessage(err, "We couldn't create your account. Check your details and try again.");

                    toast.error(message);
                });
        } else {
        }
    });

    // Helper to update fields
    const handleChange = (field, value) => {
      setFormValues((prev) => ({ ...prev, [field]: value }));
    };

    return (
        <div className="login-page" style={{ height: "100vh" }}>
            <Loader active={activeLoader}/>
            <header className="home-nav">
              <Link to="/" className="home-nav-left" aria-label="Youro home">
                <div className="home-nav-logo-circle">Y</div>
                <span className="home-nav-logo-text">Youro</span>
              </Link>
              <nav className="home-nav-center">
                <button className="home-nav-link" onClick={() => navigate("/about")}>About</button>
                <button className="home-nav-link" onClick={() => navigate("/conditions")}>Find Your Diagnosis</button>
                <button className="home-nav-link" onClick={() => navigate("/assessment")}>Symptom Assessment</button>
              </nav>
              <div className="home-nav-right">
                <button className="home-nav-signin" onClick={() => navigate("/login")}>Sign In</button>
                <button className="home-nav-get-started" onClick={() => navigate("/signup")}>Get Started</button>
              </div>
            </header>

            <main className="login-main" style={{ paddingTop: "28px" }}>
                <div style={{ maxWidth: "42rem", width: "100%", marginLeft: "auto", marginRight: "auto", paddingLeft: "1rem", paddingRight: "1rem" }}>
                    <div style={{ opacity: 1, transform: "none" }}>
                        <button className="login-back" type="button" onClick={() => navigate("/")} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", whiteSpace: "nowrap", borderRadius: "8px", fontSize: "0.875rem", fontWeight: 500, height: "36px", padding: "0 1rem", marginTop: "0.35rem", marginBottom: "0.35rem", cursor: "pointer" }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginRight: "0.5rem" }} >
                              <path d="m12 19-7-7 7-7" />
                              <path d="M19 12H5" />
                            </svg>
                            Back to Home
                        </button>

                        <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.35rem",
                              borderRadius: "12px",
                              border: "1px solid #e5e7eb",
                              backgroundColor: "#ffffff",
                              color: "#111827",
                            }}
                        >
                            {!outsideServedArea && (
                            <div
                              style={{
                                display: "grid",
                                gap: "0.375rem",
                                padding: "1.5rem",
                                textAlign: "center",
                                paddingBottom: "0.5rem"
                              }}
                            >
                              <h4 style={{ fontSize: "1.5rem", margin: 0 }}>
                                Join Youro - Step {step} of 2
                              </h4>
                              <p style={{ color: "#6b7280", margin: 0 }}>
                                {step === 1 ? "Create your account to start your health journey" : "Complete your profile and consent forms"}
                              </p>
                            </div>
                            )}

                            <div style={{ padding: outsideServedArea ? "1.5rem" : "0 1.5rem 1.5rem" }}>
                              {step === 1 && !residence && (
                                <form className="gate-panel" onSubmit={confirmResidence}>
                                  <div className="gate-field">
                                    <label htmlFor="residence-state">What state do you live in?</label>
                                    <select
                                      id="residence-state"
                                      required
                                      value={gateChoice}
                                      onChange={(e) => setGateChoice(e.target.value)}
                                    >
                                      <option value="">Select your state</option>
                                      {US_STATES.map((s) => (
                                        <option key={s.code} value={s.code}>{s.name}</option>
                                      ))}
                                    </select>
                                    <span className="gate-hint">
                                      Where you'll be during your appointments. Providers can only treat you in a
                                      state where they are licensed.
                                    </span>
                                  </div>
                                  <button type="submit" className="gate-btn-primary" disabled={!gateChoice}>
                                    Continue
                                  </button>
                                </form>
                              )}

                              {step === 1 && outsideServedArea && (
                                <WaitlistForm stateCode={residence} onChangeState={changeResidence} />
                              )}

                              {step === 1 && residence && !outsideServedArea && (
                                  <form onSubmit={handleStep1Submit} style={{ display: "flex", flexDirection: "column", gap: "0.8rem" }}>
                                    <p className="gate-chosen">
                                      You live in <strong>{stateName(residence)}</strong>.{" "}
                                      <button type="button" className="gate-link" onClick={changeResidence}>Change</button>
                                    </p>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                      <label htmlFor="signup-for" style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                                        I am:
                                      </label>
                                      <select
                                        id="signup-for"
                                        value={signupType}
                                        onChange={(e) => {setSignupType(e.target.value); setErrors({});}}
                                        onFocus={() => setFocusedField("signUpFor")}
                                        onBlur={() => setFocusedField(null)}
                                        style={{
                                          width: "100%",
                                          height: "36px",
                                          borderRadius: "6px",
                                          border: "1px solid #e5e7eb",
                                          padding: "0 0.75rem",
                                          fontSize: "0.875rem",
                                          boxShadow: focusedField === "signUpFor" ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none"
                                        }}
                                      >
                                        <option style={{ borderRadius: "6px" }} value="myself">Signing Up For Myself</option>
                                        <option style={{ borderRadius: "6px" }} value="family">Signing Up For a Family Member</option>
                                      </select>
                                    </div>

                                    {signupType === "myself" ? (
                                      <>
                                        <div className="signup-field-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                                          {["firstName", "lastName"].map((field) => (
                                            <div key={field} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                              <label htmlFor={field} style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                                                {field === "firstName" ? "First Name" : "Last Name"}
                                              </label>
                                              <input
                                                id={field}
                                                aria-invalid={Boolean(errors[field])}
                                                placeholder={field === "firstName" ? "John" : "Doe"}
                                                required
                                                value={formValues[field]}
                                                onChange={(e) => setFormValues((prev) => ({ ...prev, [field]: e.target.value })) }
                                                onFocus={() => setFocusedField(field)}
                                                onBlur={() => setFocusedField(null)}
                                                style={{ height: "36px", borderRadius: "6px", border: "1px solid #e5e7eb", padding: "0 0.75rem", boxShadow: focusedField === field ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none" }}
                                              />

                                              {errors[field] && (
                                                  <span style={{ color: "var(--youro-error)", fontSize: "0.75rem" }}>{errors[field]}</span>
                                              )}
                                            </div>
                                          ))}
                                        </div>

                                        {[
                                          ["email", "Email", "email", "john@example.com"],
                                          ["phoneNumber", "Phone Number", "tel", "5551234567"],
                                        ].map(([field, label, type, placeholder]) => (
                                          <div key={field} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                            <label htmlFor={field} style={{ fontSize: "0.875rem", fontWeight: 500 }}>{label}</label>
                                            <input id={field} aria-invalid={Boolean(errors[field])} type={type} placeholder={placeholder} required value={formValues[field]} onChange={(e) => setFormValues((prev) => ({ ...prev, [field]: e.target.value })) } onFocus={() => setFocusedField(field)} onBlur={() => setFocusedField(null)} style={{ height: "36px", borderRadius: "6px", border: "1px solid #e5e7eb", padding: "0 0.75rem", boxShadow: focusedField === field ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none", }}  />
                                            {/* Error Warning */}
                                           {errors[field] && (
                                                <span style={{ color: "var(--youro-error)", fontSize: "0.75rem" }}>{errors[field]}</span>
                                            )}
                                          </div>
                                        ))}
                                      </>
                                    ) : (
                                      <>
                                        <div>
                                          <label style={{ fontSize: "0.875rem", fontWeight: 600 }}>
                                            Person Registering (You)
                                          </label>
                                        </div>

                                        <div className="signup-field-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                                          {["firstName", "lastName"].map((field) => (
                                            <div key={field} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                              <label htmlFor={field} style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                                                {field === "firstName" ? "First Name" : "Last Name"}
                                              </label>
                                              <input
                                                id={field}
                                                aria-invalid={Boolean(errors[field])}
                                                placeholder={field === "firstName" ? "John" : "Doe"}
                                                required
                                                value={formValues[field]}
                                                onChange={(e) => setFormValues((prev) => ({ ...prev, [field]: e.target.value })) }
                                                onFocus={() => setFocusedField(field)}
                                                onBlur={() => setFocusedField(null)}
                                                style={{ height: "36px", borderRadius: "6px", border: "1px solid #e5e7eb", padding: "0 0.75rem", boxShadow: focusedField === field ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none" }}
                                              />

                                               {errors[field] && (
                                                  <span style={{ color: "var(--youro-error)", fontSize: "0.75rem" }}>{errors[field]}</span>
                                              )}
                                            </div>
                                          ))}
                                        </div>

                                        <div>
                                          <label style={{ fontSize: "0.875rem", fontWeight: 600 }}>
                                            Patient Information
                                          </label>
                                        </div>

                                        <div className="signup-field-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                                          {["patfirstName", "patlastName"].map((field) => (
                                            <div key={field} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                              <label htmlFor={field} style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                                                {field === "patfirstName" ? "Patient First Name" : "Patient Last Name"}
                                              </label>
                                              <input
                                                id={field}
                                                aria-invalid={Boolean(errors[field])}
                                                placeholder={field === "patfirstName" ? "Jane" : "Smith"}
                                                required value={formValues[field]}
                                                onChange={(e) => setFormValues((prev) => ({ ...prev, [field]: e.target.value })) }
                                                onFocus={() => setFocusedField(field)} onBlur={() => setFocusedField(null)}
                                                style={{ height: "36px", borderRadius: "6px", border: "1px solid #e5e7eb", padding: "0 0.75rem", boxShadow: focusedField === field ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none", }}
                                              />

                                               {errors[field] && (
                                                  <span style={{ color: "var(--youro-error)", fontSize: "0.75rem" }}>{errors[field]}</span>
                                              )}
                                            </div>
                                          ))}
                                        </div>

                                        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                          <label htmlFor="signup-relation" style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                                            Relationship to Patient:
                                          </label>
                                          <select
                                            id="signup-relation"
                                            required
                                            value={formValues.relation}
                                            onChange={(e) => handleChange("relation", e.target.value)}
                                            onFocus={() => setFocusedField("relation")}
                                            onBlur={() => setFocusedField(null)}
                                            style={{ width: "100%", height: "36px", borderRadius: "6px", border: "1px solid #e5e7eb", padding: "0 0.75rem", fontSize: "0.875rem", boxShadow: focusedField === "relation" ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none" }}
                                          >
                                            <option value="">Select Relationship</option>
                                            <option value="Parent">Parent</option>
                                            <option value="Spouse">Spouse</option>
                                            <option value="Child">Child</option>
                                            <option value="Sibling">Sibling</option>
                                            <option value="Legal Guardian">Legal Guardian</option>
                                            <option value="Caregiver">Caregiver</option>
                                            <option value="Other">Other</option>
                                          </select>
                                        </div>

                                        {[
                                          ["email", "Preferred Email", "email", "smith@example.com"],
                                          ["phoneNumber", "Preferred Phone Number", "tel", "7161234567"],
                                        ].map(([field, label, type, placeholder]) => (
                                          <div key={field} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                            <label htmlFor={field} style={{ fontSize: "0.875rem", fontWeight: 500 }}>{label}</label>
                                            <input id={field} aria-invalid={Boolean(errors[field])} type={type} placeholder={placeholder} required value={formValues[field]}
                                              onChange={(e) => setFormValues((prev) => ({ ...prev, [field]: e.target.value })) } onFocus={() => setFocusedField(field)}
                                              onBlur={() => setFocusedField(null)}
                                              style={{ height: "36px", borderRadius: "6px", border: "1px solid #e5e7eb", padding: "0 0.75rem", boxShadow: focusedField === field ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none", }}
                                            />
                                            {errors[field] && (
                                                <span style={{ color: "var(--youro-error)", fontSize: "0.75rem" }}>{errors[field]}</span>
                                            )}
                                          </div>
                                        ))}
                                      </>
                                    )}

                                    {[
                                      ["password", "Password", "password", "••••••••"],
                                      ["confirmPassword", "Re-type Password", "password", "••••••••"],
                                    ].map(([field, label, type, placeholder]) => (
                                      <div key={field} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                                        <label htmlFor={field} style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                                          {label}
                                        </label>
                                        <input
                                          id={field}
                                          aria-invalid={Boolean(errors[field])}
                                          type={type}
                                          placeholder={placeholder}
                                          required
                                          value={formValues[field]}
                                          onChange={(e) =>
                                            setFormValues((prev) => ({ ...prev, [field]: e.target.value }))
                                          }
                                          onFocus={() => setFocusedField(field)}
                                          onBlur={() => setFocusedField(null)}
                                          style={{
                                            height: "36px",
                                            borderRadius: "6px",
                                            border: "1px solid #e5e7eb",
                                            padding: "0 0.75rem",
                                            boxShadow: focusedField === field ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none",
                                          }}
                                        />

                                        {errors[field] && (
                                          <span style={{ color: "var(--youro-error)", fontSize: "0.75rem" }}>{errors[field]}</span>
                                       )}
                                      </div>
                                    ))}


                                    <button
                                      type="submit"
                                      disabled={!isStep1Filled}
                                      title={!isStep1Filled ? "Please complete all fields" : ""}
                                      onMouseEnter={() => setIsHovered(true)}
                                      onMouseLeave={() => setIsHovered(false)}
                                      style={{
                                        width: "100%",
                                        height: "36px",
                                        borderRadius: "6px",
                                        fontWeight: 500,
                                        backgroundColor: isStep1Filled ? "var(--youro-coral)" : "var(--youro-stone)",
                                        color: isStep1Filled ? "var(--youro-card)" : "var(--youro-charcoal)",
                                        border: "none",
                                        cursor: isStep1Filled ? "pointer" : "not-allowed",
                                        transition: "all 0.2s ease",
                                        transform: isHovered && isStep1Filled ? "scale(1.02)" : "scale(1)",
                                      }}
                                    >
                                      Continue
                                      <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="lucide lucide-arrow-right ml-2 w-4 h-4"
                                          aria-hidden="true"
                                        >
                                          <path d="M5 12h14" />
                                          <path d="m12 5 7 7-7 7" />
                                        </svg>
                                    </button>

                                    <div style={{ textAlign: "center", fontSize: "0.875rem", color: "#6b7280" }}>
                                      Already have an account?{" "}
                                      <button
                                        type="button"
                                        style={{
                                          color: "var(--youro-sky)",
                                          background: "none",
                                          border: "none",
                                          cursor: "pointer",
                                          padding: 0,
                                          transition: "text-decoration 0.2s ease, color 0.2s ease",
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                                        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                                        onClick={() => navigate("/login")}
                                      >
                                        Sign in
                                      </button>
                                    </div>
                                  </form>

                              )}

{/* ================= STEP 2 FORM ================= */}
              {step === 2 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

                  {signupType === "family" && (
                    <div><label style={{ fontSize: "0.875rem", fontWeight: 600 }}>Patient Information</label></div>
                  )}

                  {/* DOB */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <label htmlFor="signup-dob" style={{ fontSize: "0.875rem", fontWeight: 500 }}>{signupType === "family" ? "Patient " : ""}Date of Birth</label>
                    <input
                      id="signup-dob"
                      type="date"
                      required
                      value={formValues.dateOfBirth}
                      onChange={(e) => handleChange("dateOfBirth", e.target.value)}
                      onFocus={() => setFocusedField("dateOfBirth")}
                      onBlur={() => setFocusedField(null)}
                      style={{ height: "36px", borderRadius: "6px", border: "1px solid #e5e7eb", padding: "0 0.75rem", boxShadow: focusedField === "dateOfBirth" ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none", color: "#6b7280", fontFamily: "inherit" }}
                    />
                  </div>

                  {/* Sex */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <label htmlFor="signup-sex" style={{ fontSize: "0.875rem", fontWeight: 500 }}>{signupType === "family" ? "Patient " : ""}Biological Sex</label>
                    <select
                      id="signup-sex"
                      required
                      value={formValues.gender}
                      onChange={(e) => handleChange("gender", e.target.value)}
                      onFocus={() => setFocusedField("gender")}
                      onBlur={() => setFocusedField(null)}
                      style={{ width: "100%", height: "36px", borderRadius: "6px", border: "1px solid #e5e7eb", padding: "0 0.75rem", fontSize: "0.875rem", boxShadow: focusedField === "gender" ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none" }}
                    >
                      <option value="">Select biological sex</option>
                      <option value="MALE">Male</option>
                      <option value="FEMALE">Female</option>
                      <option value="OTHER">Intersex</option>
                    </select>
                  </div>

                  {/* Address */}
                  {signupType === "family" && (
                     <div><label style={{ fontSize: "0.875rem", fontWeight: 500 }}>Patient Address</label></div>
                  )}
                  {signupType === "myself" && (
                     <div><label style={{ fontSize: "0.875rem", fontWeight: 500 }}>Address</label></div>
                  )}

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <label htmlFor="signup-address1" style={{ fontSize: "0.875rem", fontWeight: 500 }}>Address Line 1</label>
                    <input
                      id="signup-address1"
                      placeholder="123 Main Street"
                      required
                      value={formValues.address}
                      onChange={(e) => handleChange("address", e.target.value)}
                      onFocus={() => setFocusedField("address")}
                      onBlur={() => setFocusedField(null)}
                      style={{ height: "36px", borderRadius: "6px", border: "1px solid #e5e7eb", padding: "0 0.75rem", boxShadow: focusedField === "address" ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none" }}
                    />
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <label htmlFor="signup-address2" style={{ fontSize: "0.875rem", fontWeight: 500 }}>Address Line 2 (Optional)</label>
                    <input
                      id="signup-address2"
                      placeholder="Apt 2B, Suite 100, etc."
                      value={formValues.address2}
                      onChange={(e) => handleChange("address2", e.target.value)}
                      onFocus={() => setFocusedField("address2")}
                      onBlur={() => setFocusedField(null)}
                      style={{ height: "36px", borderRadius: "6px", border: "1px solid #e5e7eb", padding: "0 0.75rem", boxShadow: focusedField === "address2" ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none" }}
                    />
                  </div>

                  <div className="signup-field-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <label htmlFor="signup-city" style={{ fontSize: "0.875rem", fontWeight: 500 }}>City</label>
                      <input
                        id="signup-city"
                        placeholder="New York"
                        required
                        value={formValues.city}
                        onChange={(e) => handleChange("city", e.target.value)}
                        onFocus={() => setFocusedField("city")}
                        onBlur={() => setFocusedField(null)}
                        style={{ height: "36px", borderRadius: "6px", border: "1px solid #e5e7eb", padding: "0 0.75rem", boxShadow: focusedField === "city" ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none" }}
                      />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <label htmlFor="signup-state" style={{ fontSize: "0.875rem", fontWeight: 500 }}>State</label>
                      {/* Fixed to the state chosen before signup began. A dropdown
                          here let someone pick South Carolina at the start and
                          another state now. */}
                      <input
                        id="signup-state"
                        readOnly
                        value={stateName(formValues.state)}
                        aria-describedby="signup-state-hint"
                        style={{ height: "36px", borderRadius: "6px", border: "1px solid #e5e7eb", padding: "0 0.75rem", backgroundColor: "var(--youro-sand)" }}
                      />
                      <span id="signup-state-hint" className="gate-hint">From the start of signup. To change it, go back to step 1.</span>
                    </div>
                  </div>

                  <div key={"zipCode"} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <label htmlFor="signup-zip" style={{ fontSize: "0.875rem", fontWeight: 500 }}>Zip Code</label>
                    <input
                      id="signup-zip"
                      placeholder="10001"
                      required
                      value={formValues.zipCode}
                      onChange={(e) => handleChange("zipCode", e.target.value)}
                      onFocus={() => setFocusedField("zipCode")}
                      onBlur={() => setFocusedField(null)}
                      style={{ width: "50%", height: "36px", borderRadius: "6px", border: "1px solid #e5e7eb", padding: "0 0.75rem", boxShadow: focusedField === "zipCode" ? "0 0 0 3px rgba(143, 168, 122, 0.6)" : "none" }}
                    />
                    {errors["zipCode"] && (
                      <span style={{ color: "var(--youro-error)", fontSize: "0.75rem" }}>{errors["zipCode"]}</span>
                    )}
                  </div>

                  {/* Consents */}
                  <div style={{ marginTop: "1rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                    <label style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontSize: "0.875rem", color: "#4b5563", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={formValues.agreedPrivacy}
                        onChange={(e) => handleChange("agreedPrivacy", e.target.checked)}
                        style={{ marginTop: "0.2rem" }}
                      />
                      <span>I agree to the{" "}
                        <a
                          href="/policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            color: "var(--youro-sky)",
                            fontWeight: 500,
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          Privacy Policy
                        </a>
                      </span>
                    </label>

                    <label style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontSize: "0.875rem", color: "#4b5563", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={formValues.agreedTelehealth}
                        onChange={(e) => handleChange("agreedTelehealth", e.target.checked)}
                        style={{ marginTop: "0.2rem" }}
                      />
                      <span>I consent to{" "}
                        <a
                          href="/telehealth-consent"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            color: "var(--youro-sky)",
                            fontWeight: 500,
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          Telehealth Services
                        </a>
                      </span>
                    </label>

                    <label style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontSize: "0.875rem", color: "#4b5563", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={formValues.agreedTerms}
                        onChange={(e) => handleChange("agreedTerms", e.target.checked)}
                        style={{ marginTop: "0.2rem" }}
                      />
                      <span>I agree to the{" "}
                        <a
                          href="/terms-conditions"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            color: "var(--youro-sky)",
                            fontWeight: 500,
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          Terms & Conditions
                        </a>
                      </span>
                    </label>
                  </div>

                  {/* Action Buttons */}
                  <div className="signup-field-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr", gap: "1rem", marginTop: "1rem" }}>
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      style={{
                        height: "40px",
                        borderRadius: "6px",
                        fontWeight: 500,
                        backgroundColor: "#f3f4f6",
                        color: "#374151",
                        border: "1px solid #e5e7eb",
                        cursor: "pointer"
                      }}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      disabled={!isStep2Valid || registering}
                      aria-busy={registering}
                      title={!isStep2Valid ? "Please complete all fields" : ""}
                      onClick={handleFinalSubmit}
                      onMouseEnter={() => setIsHovered(true)}
                      onMouseLeave={() => setIsHovered(false)}
                      style={{
                        height: "40px",
                        borderRadius: "6px",
                        fontWeight: 500,
                        backgroundColor: isStep2Valid ? "var(--youro-coral)" : "var(--youro-stone)",
                        color: isStep2Valid ? "var(--youro-card)" : "var(--youro-charcoal)",
                        border: "none",
                        cursor: isStep2Valid ? "pointer" : "not-allowed",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem",
                        transition: "all 0.2s ease",
                        transform: isHovered && isStep2Valid ? "scale(1.02)" : "scale(1)",
                      }}
                    >
                      Create Account
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14" />
                        <path d="m12 5 7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                </div>
              )}


                            </div>
                        </div>
                    </div>
                </div>
            </main>
    </div>
    )
}

export default Signupoptions;